import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, Download, Play, GitCompare, ShoppingCart, FileText } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import GatedDownloadModal from './GatedDownloadModal';
import VideoModal from './VideoModal';
import ResponsiveCarousel from './ResponsiveCarousel';
import SEO, { productJsonLd, breadcrumbJsonLd } from './SEO';
import { WhereToBuy, OtherBuyingOptions, trackPartnerContact } from './DistributionPartners';
import { quoteProduct } from '../data/quoteProducts';
import CompareCamerasModal from './CompareCamerasModal';
import StickyCompareBar from './StickyCompareBar';
import BoeingToolCoverage from './BoeingToolCoverage';

const ProductPage = ({ 
  productName, 
  partNumber,
  boeingToolTag,
  tagline, 
  description,
  metaDescription, 
  features, 
  specifications,
  applications,
  caseStudy,
  caseStudies,
  benefits,
  relatedProducts,
  image,
  imageContain,
  images,
  videoEmbed,
  videoAspect,
  productImage,
  heroImage,
  dataSheetUrl,
  heroExtra,
  whereToBuy = false,
  buyNow,
  compareCameraId,
  boeingCoverage,
  extraSections,
  afterDetails,
  productFaq,
  onRequestDemo,
  onRequestQuote 
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [gatedOpen, setGatedOpen] = useState(false);
  const [gatedAsset, setGatedAsset] = useState(null); // {title, url} for case-study PDFs
  const [videoAsset, setVideoAsset] = useState(null); // {url, title} for case-study videos
  const [caseIndex, setCaseIndex] = useState(0);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareOtherId, setCompareOtherId] = useState(undefined);
  const hasCarousel = Array.isArray(images) && images.length > 0;
  const hasCaseStudies = Array.isArray(caseStudies) && caseStudies.length > 0;

  // Tabbed info section
  const infoTabs = [
    { id: 'specifications', label: 'Technical Specifications', show: !!specifications },
    { id: 'features', label: 'Key Features', show: !!features },
    { id: 'applications', label: 'Aviation Mx Applications', show: !!applications },
    { id: 'benefits', label: 'Why the UL101?', show: !!benefits },
  ].filter((t) => t.show);
  const [activeTab, setActiveTab] = useState(infoTabs[0]?.id || 'specifications');

  const imagesCount = Array.isArray(images) ? images.length : 0;
  useEffect(() => {
    if (!hasCarousel) return;
    const timer = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % imagesCount);
    }, 5000);
    return () => clearInterval(timer);
  }, [hasCarousel, imagesCount]);

  const goPrev = () => setCarouselIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setCarouselIndex((i) => (i + 1) % images.length);

  // On product pages we forward the product name into the parent CTA
  // handler so events fired here carry a `product` attribute (used by
  // the "Top products" panel in /admin/analytics).
  const handleDemo = () => onRequestDemo && onRequestDemo({ product: productName });
  const handleQuote = () => onRequestQuote && onRequestQuote({ product: productName, prefill: { product: buyNow || productName } });
  const buyNowProduct = buyNow ? quoteProduct(buyNow) : null;
  const handleBuyBarfield = () => onRequestQuote && onRequestQuote({ product: productName, resource: 'buy-now-barfield', prefill: { product: buyNow, sources: ['barfield'], lockedSources: ['barfield'] } });

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${productName} — ctrl.aero`}
        description={metaDescription || (typeof description === 'string' ? description : tagline)}
        canonical={`https://ctrl.aero${pathname}`}
        image={productImage || heroImage || image}
        jsonLd={[
          productJsonLd({
            name: productName,
            description: typeof description === 'string' ? description : tagline,
            image: productImage || heroImage || image,
            brand: 'CTRL Systems',
            sku: partNumber,
            url: typeof window !== 'undefined' ? `https://ctrl.aero${window.location.pathname}` : undefined,
          }),
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: 'Solutions', url: 'https://ctrl.aero/#solutions' },
            {
              name: productName,
              url: typeof window !== 'undefined' ? `https://ctrl.aero${window.location.pathname}` : 'https://ctrl.aero/',
            },
          ]),
        ]}
      />
      <Header onRequestDemo={handleDemo} onRequestQuote={handleQuote} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 mb-8 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </button>
          
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Copy column — appears BELOW the media on mobile so users see
                the product first, then read.  On lg+ it moves back to the
                left. */}
            <div className="order-2 lg:order-1">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight leading-[1.1]">{productName}</h1>
              {(partNumber || boeingToolTag) && (
                <div className="flex flex-wrap gap-2 sm:gap-4 mb-4">
                  {partNumber && (
                    <span className="inline-block whitespace-nowrap px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-blue-300 text-sm sm:text-base font-medium" data-testid="product-pn-badge">
                      P/N: {partNumber}
                    </span>
                  )}
                  {boeingToolTag && (
                    <span className="inline-block whitespace-nowrap px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-blue-300 text-sm sm:text-base font-medium" data-testid="product-boeing-badge">
                      Boeing: {boeingToolTag}
                    </span>
                  )}
                </div>
              )}
              <p className="text-lg sm:text-xl lg:text-2xl text-blue-300 mb-6 max-w-prose">{tagline}</p>
              {heroExtra}
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8 max-w-prose">{description}</p>
              
              <div className="max-w-4xl space-y-3">
                <div className="cta-group cta-group-compact">
                  {dataSheetUrl && (
                    <Button 
                      onClick={() => setGatedOpen(true)}
                      data-testid="product-datasheet-btn"
                      size="lg"
                      className="bg-slate-700 hover:bg-slate-800 text-white px-6 text-sm sm:text-[15px] font-semibold"
                    >
                      Download Data Sheet
                    </Button>
                  )}
                  {onRequestDemo && (
                    <Button 
                      onClick={handleDemo}
                      size="lg"
                      data-testid="hero-request-demo-btn"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 text-sm sm:text-[15px] font-semibold"
                    >
                      Request Demo
                    </Button>
                  )}
                  <Button 
                    onClick={handleQuote}
                    size="lg"
                    data-testid="hero-request-quote-btn"
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 text-sm sm:text-[15px] font-semibold"
                  >
                    Request Quote
                  </Button>
                  {compareCameraId && (
                    <Button
                      onClick={() => setCompareOpen(true)}
                      data-testid="compare-cameras-btn"
                      size="lg"
                      variant="outline"
                      className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-6 text-sm sm:text-[15px] font-semibold"
                    >
                      <GitCompare className="w-5 h-5 mr-2" />
                      Compare Models
                    </Button>
                  )}
                </div>
                {buyNowProduct && (
                  <div className="cta-group cta-group-compact" data-testid="buy-now-row">
                    <Button
                      onClick={handleBuyBarfield}
                      size="lg"
                      data-testid="buy-now-barfield-btn"
                      className="bg-white text-[#1e2d3b] hover:bg-blue-50 px-6 text-sm sm:text-[15px] font-semibold"
                    >
                      <FileText className="w-5 h-5 mr-2" />
                      Request Quote from Barfield
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      className="bg-white text-[#1e2d3b] hover:bg-blue-50 px-6 text-sm sm:text-[15px] font-semibold"
                    >
                      <a href={buyNowProduct.avionteq} target="_blank" rel="noopener noreferrer" onClick={() => trackPartnerContact('AvionTEq', 'website')} data-testid="buy-now-avionteq-btn">
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Order Now from AvionTEq
                      </a>
                    </Button>
                  </div>
                )}
              </div>
              {buyNowProduct ? <OtherBuyingOptions /> : whereToBuy && <WhereToBuy />}
            </div>
            
            {/* Media column — first on mobile, right on desktop. */}
            <div className="order-1 lg:order-2 space-y-5">
              {heroImage && (
                <div className="rounded-2xl overflow-hidden shadow-2xl bg-white" data-testid="hero-product-image">
                  <img src={heroImage} alt={`${productName} - Product Detail`} className="w-full h-auto object-contain max-h-[26rem]" fetchpriority="high" />
                </div>
              )}
              {videoEmbed ? (
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <div style={{ padding: `${videoAspect || 56.25}% 0 0 0`, position: 'relative' }}>
                  <iframe 
                    src={videoEmbed}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title={`${productName} - Video Demo`}
                  />
                </div>
              </div>
            ) : hasCarousel ? (
              <div className="relative group">
                <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-white h-72 sm:h-96">
                  {images.map((img, idx) => (
                    <img
                      key={img}
                      src={img}
                      alt={`${productName} - Image ${idx + 1}`}
                      className={`absolute inset-0 w-full h-full object-contain p-6 transition-opacity duration-700 ease-in-out ${idx === carouselIndex ? 'opacity-100' : 'opacity-0'}`}
                      data-testid={`product-carousel-image-${idx}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous image"
                  data-testid="product-carousel-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-lg flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next image"
                  data-testid="product-carousel-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 hover:bg-white text-slate-800 shadow-lg flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                  {images.map((img, idx) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => setCarouselIndex(idx)}
                      aria-label={`Go to image ${idx + 1}`}
                      data-testid={`product-carousel-dot-${idx}`}
                      className={`h-2 rounded-full transition-all duration-300 ${idx === carouselIndex ? 'w-8 bg-blue-600' : 'w-2 bg-white/70 hover:bg-white'}`}
                    />
                  ))}
                </div>
              </div>
            ) : image ? (
              <div className="relative">
                <img 
                  src={image}
                  alt={productName}
                  className={`rounded-2xl shadow-2xl w-full h-72 sm:h-96 ${imageContain ? 'object-contain bg-white p-4 sm:p-6' : 'object-cover'}`}
                />
              </div>
            ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Product Image Section */}
      {productImage && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <img 
                src={productImage}
                alt={`${productName} - Product Detail`}
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      {/* Case Studies Carousel — Real-World Success Stories */}
      {hasCaseStudies && (
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold uppercase tracking-wide mb-4">
                Real-World Success Stories
              </span>
              <h2 className="text-4xl font-bold text-[#1e2d3b] mb-2">
                {caseStudies[caseIndex].title}
              </h2>
              <p className="text-slate-500 text-sm">
                Case Study {caseIndex + 1} of {caseStudies.length}
              </p>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-6 md:p-10 border-2 border-blue-100">
                <div className="grid lg:grid-cols-3 gap-6 mb-8">
                  {/* Problem */}
                  <div className="bg-white rounded-2xl p-6 border-l-4 border-orange-500 flex flex-col">
                    <h3 className="text-xl font-bold text-[#1e2d3b] mb-4 flex items-center">
                      <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">1</span>
                      The Problem
                    </h3>
                    <p className="text-slate-700 leading-relaxed text-sm">
                      {caseStudies[caseIndex].problem}
                    </p>
                  </div>

                  {/* Solution */}
                  <div className="bg-white rounded-2xl p-6 border-l-4 border-blue-500 flex flex-col">
                    <h3 className="text-xl font-bold text-[#1e2d3b] mb-4 flex items-center">
                      <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">2</span>
                      The Solution
                    </h3>
                    <p className="text-slate-700 leading-relaxed text-sm">
                      {caseStudies[caseIndex].solution}
                    </p>
                  </div>

                  {/* Results */}
                  <div className="bg-white rounded-2xl p-6 border-l-4 border-green-500 flex flex-col">
                    <h3 className="text-xl font-bold text-[#1e2d3b] mb-4 flex items-center">
                      <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">3</span>
                      The Results
                    </h3>
                    <ul className="space-y-2">
                      {caseStudies[caseIndex].results.map((result, idx) => (
                        <li key={result} className="flex items-start space-x-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700 text-sm">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="cta-group cta-group-sm justify-center max-w-2xl mx-auto">
                  {caseStudies[caseIndex].pdfs && caseStudies[caseIndex].pdfs.map((pdf, idx) => (
                    <Button
                      key={pdf.url}
                      onClick={() => setGatedAsset({ title: pdf.title, url: pdf.url })}
                      data-testid={`case-study-pdf-${caseIndex}-${idx}`}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-4 text-sm shadow-lg"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      {pdf.label}
                    </Button>
                  ))}
                  {caseStudies[caseIndex].videoUrl && (
                    <Button
                      onClick={() => setVideoAsset({ url: caseStudies[caseIndex].videoUrl, title: caseStudies[caseIndex].title })}
                      data-testid={`case-study-video-${caseIndex}`}
                      className="bg-[#1e2d3b] hover:bg-[#15202b] text-white font-semibold px-4 text-sm shadow-lg"
                    >
                      <Play className="mr-2 h-5 w-5" />
                      Watch the Video
                    </Button>
                  )}
                </div>
              </div>

              {/* Arrows */}
              {caseStudies.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setCaseIndex((i) => (i - 1 + caseStudies.length) % caseStudies.length)}
                    aria-label="Previous case study"
                    data-testid="case-study-prev"
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-12 h-12 rounded-full bg-white shadow-xl text-blue-600 flex items-center justify-center hover:scale-110 transition-all duration-200 ring-2 ring-blue-100"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCaseIndex((i) => (i + 1) % caseStudies.length)}
                    aria-label="Next case study"
                    data-testid="case-study-next"
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-12 h-12 rounded-full bg-white shadow-xl text-blue-600 flex items-center justify-center hover:scale-110 transition-all duration-200 ring-2 ring-blue-100"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>

            {/* Dots */}
            {caseStudies.length > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {caseStudies.map((cs, idx) => (
                  <button
                    key={cs.title || cs.id || idx}
                    type="button"
                    onClick={() => setCaseIndex(idx)}
                    aria-label={`Case study ${idx + 1}`}
                    data-testid={`case-study-dot-${idx}`}
                    className={`h-2 rounded-full transition-all duration-300 ${idx === caseIndex ? 'w-8 bg-blue-600' : 'w-2 bg-slate-300 hover:bg-blue-400'}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Boeing Tool Coverage (SPL-* / COM-*) — surfaced above the tabs so
          MRO tooling engineers see it during their equivalency review. */}
      {boeingCoverage && <BoeingToolCoverage coverage={boeingCoverage} />}
      {extraSections}

      {/* Tabbed product info: Features / Specs / Applications / Benefits */}
      {infoTabs.length > 0 && (
        <section className="py-16 sm:py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] mb-3">Product Details</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto"></div>
            </div>

            {/* Tab bar */}
            <div className="border-b border-slate-200 mb-8 sm:mb-10">
              <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
                {infoTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`product-tab-${tab.id}`}
                    className={`relative px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'text-blue-600'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <span className="absolute bottom-[-1px] left-2 right-2 h-1 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab panels — all rendered in the DOM (only the active one is
                visible) so non-JS crawlers can see every panel's content. */}
            <div className="min-h-[280px]">
              {features && (
                <div
                  role="tabpanel"
                  aria-labelledby="tab-features"
                  hidden={activeTab !== 'features'}
                  className={`grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 animate-fade-in ${activeTab === 'features' ? '' : 'hidden'}`}
                >
                  <h2 className="sr-only">Key Features</h2>
                  {features.map((feature, index) => (
                    <div key={feature.title || index} className="p-5 sm:p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-blue-100">
                      <div className="flex items-start space-x-3">
                        <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-[#1e2d3b] mb-2 text-base sm:text-lg">{feature.title}</h3>
                          <p className="text-slate-600 text-sm sm:text-base">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {specifications && (
                <div
                  role="tabpanel"
                  aria-labelledby="tab-specifications"
                  hidden={activeTab !== 'specifications'}
                  className={`bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden animate-fade-in ${activeTab === 'specifications' ? '' : 'hidden'}`}
                >
                  <h2 className="sr-only">Technical Specifications</h2>
                  <div className="grid md:grid-cols-2">
                    {specifications.map((spec, index) => (
                      <div
                        key={spec.label || index}
                        className="flex justify-between items-center px-5 sm:px-6 py-4 border-b border-slate-100 odd:bg-slate-50/50 md:[&:nth-last-child(-n+2)]:border-b-0"
                      >
                        <span className="font-semibold text-[#1e2d3b] text-sm sm:text-base">{spec.label}</span>
                        <span className="text-slate-600 text-sm sm:text-base text-right ml-3">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {applications && (
                <div
                  role="tabpanel"
                  aria-labelledby="tab-applications"
                  hidden={activeTab !== 'applications'}
                  className={`grid md:grid-cols-2 gap-3 sm:gap-4 animate-fade-in ${activeTab === 'applications' ? '' : 'hidden'}`}
                >
                  <h2 className="sr-only">Aviation Mx Applications</h2>
                  {applications.map((app, index) => (
                    <div key={typeof app === "string" ? app : app.title || index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-xl">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-700 text-sm sm:text-base">{app}</p>
                    </div>
                  ))}
                </div>
              )}

              {benefits && (
                <div
                  role="tabpanel"
                  aria-labelledby="tab-benefits"
                  hidden={activeTab !== 'benefits'}
                  className={`animate-fade-in ${activeTab === 'benefits' ? '' : 'hidden'}`}
                >
                  <h2 className="sr-only">Benefits</h2>
                  <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 text-center max-w-3xl mx-auto">
                    Proven results that deliver immediate value to your maintenance operations
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {benefits.map((benefit, index) => (
                      <div
                        key={benefit}
                        className="flex items-start space-x-3 p-5 sm:p-6 bg-white rounded-xl border-2 border-blue-100 hover:border-blue-500 transition-all duration-300 hover:shadow-lg"
                      >
                        <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-slate-700 font-medium text-sm sm:text-base">{benefit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      {afterDetails}

      {/* Case Study Section (single — legacy/backward compat only) */}
      {caseStudy && !hasCaseStudies && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold uppercase tracking-wide mb-4">
                Real-World Success Story
              </span>
              <h2 className="text-4xl font-bold text-[#1e2d3b] mb-4">{caseStudy.title}</h2>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 md:p-12 border-2 border-blue-100">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Problem */}
                <div className="bg-white rounded-2xl p-6 border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-[#1e2d3b] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                    The Problem
                  </h3>
                  <p className="text-slate-700 leading-relaxed">{caseStudy.problem}</p>
                </div>

                {/* Solution */}
                <div className="bg-white rounded-2xl p-6 border-l-4 border-blue-500">
                  <h3 className="text-xl font-bold text-[#1e2d3b] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                    The Solution
                  </h3>
                  <p className="text-slate-700 leading-relaxed">{caseStudy.solution}</p>
                </div>

                {/* Results */}
                <div className="bg-white rounded-2xl p-6 border-l-4 border-green-500">
                  <h3 className="text-xl font-bold text-[#1e2d3b] mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                    The Results
                  </h3>
                  <ul className="space-y-3">
                    {caseStudy.results.map((result, index) => (
                      <li key={result} className="flex items-start space-x-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Contact us today for a demonstration or to discuss your specific requirements.
          </p>
          <div className="cta-group cta-group-compact justify-center max-w-2xl mx-auto">
            <Button 
              onClick={handleDemo}
              size="lg"
              data-testid="bottom-request-demo-btn"
              className="bg-white text-blue-600 hover:bg-blue-50 px-6 text-sm sm:text-[15px] font-semibold"
            >
              Request Demo
            </Button>
            <Button 
              onClick={handleQuote}
              size="lg"
              data-testid="bottom-request-quote-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 text-sm sm:text-[15px] font-semibold"
            >
              Request Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Other Recommended Products carousel */}
      {Array.isArray(relatedProducts) && relatedProducts.filter((p) => p.image).length > 0 && (
        <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1e2d3b] mb-3">Other Recommended Products</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto"></div>
            </div>

            {(() => {
              const visible = relatedProducts.filter((p) => p.image);
              const renderCard = (p, idx) => (
                <div
                  key={p.name || idx}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full"
                  data-testid={`related-product-card-${idx}`}
                >
                  <div className="aspect-[4/3] bg-slate-50 overflow-hidden flex-shrink-0">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                  <div className="p-4 sm:p-5 flex flex-col flex-grow">
                    <h3 className="text-sm sm:text-base font-bold text-[#1e2d3b] mb-2 leading-snug">{p.name}</h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-4 flex-grow line-clamp-2">{p.description}</p>
                    <Button
                      onClick={p.onClick || (() => {})}
                      data-testid={`related-product-cta-${idx}`}
                      className={`w-full text-sm font-semibold ${
                        p.cta === 'Learn More'
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                          : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
                      }`}
                    >
                      {p.cta || 'Learn More'}
                    </Button>
                  </div>
                </div>
              );

              // With 1–3 cards, a centered grid reads better than a wide,
              // left-aligned carousel. Above that, keep the auto-scrolling
              // carousel behavior.
              if (visible.length <= 3) {
                const colClass =
                  visible.length === 1 ? 'sm:grid-cols-1 max-w-sm' :
                  visible.length === 2 ? 'sm:grid-cols-2 max-w-3xl' :
                  'sm:grid-cols-2 md:grid-cols-3 max-w-5xl';
                return (
                  <div className={`grid grid-cols-1 ${colClass} gap-6 mx-auto`}>
                    {visible.map((p, idx) => renderCard(p, idx))}
                  </div>
                );
              }

              return (
                <ResponsiveCarousel
                  breakpoints={{ base: 1, sm: 2, md: 3, lg: 4 }}
                  ariaLabel="Other recommended products"
                  dataTestId="related-products-carousel"
                  autoPlay
                  autoPlayInterval={15000}
                >
                  {visible.map((p, idx) => renderCard(p, idx))}
                </ResponsiveCarousel>
              );
            })()}
          </div>
        </section>
      )}

      {/* Product-specific FAQ (e.g. UL101 buyer FAQ).  Used when we want a
          dedicated FAQ that isn't the shared acoustic-imager one. */}
      {productFaq}

      <Footer />
      <GatedDownloadModal
        open={gatedOpen}
        onOpenChange={setGatedOpen}
        resourceTitle={`${productName} Data Sheet`}
        resourceUrl={dataSheetUrl}
      />
      <GatedDownloadModal
        open={Boolean(gatedAsset)}
        onOpenChange={(open) => { if (!open) setGatedAsset(null); }}
        resourceTitle={gatedAsset?.title}
        resourceUrl={gatedAsset?.url}
      />
      <VideoModal
        open={Boolean(videoAsset)}
        onOpenChange={(open) => { if (!open) setVideoAsset(null); }}
        videoUrl={videoAsset?.url}
        title={videoAsset?.title}
      />
      {compareCameraId && (
        <CompareCamerasModal
          open={compareOpen}
          onOpenChange={setCompareOpen}
          primaryId={compareCameraId}
          defaultOtherId={compareOtherId}
        />
      )}
      {compareCameraId && (
        <StickyCompareBar
          primaryId={compareCameraId}
          onCompareRequest={(otherId) => {
            setCompareOtherId(otherId);
            setCompareOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default ProductPage;
